import { Col, Row } from "react-bootstrap";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useGet, usePost } from "@/apiHooks/HttpFunctions";
import BasicModal from "@/Modals/BasicModal";
import RenderToast from "@/components/RenderToast";
import EventParticipantInitialValues, {
  rankTypeValues,
} from "@/data/Profiles/EventParticipants/initial-values";
import EventParticipantSchema from "@/schema/Profiles/EventParticipants/EventParticipantSchema";
import {
  eventParticipantsApi,
  teamsMasterDataApi,
  eventsMasterDataApi,
} from "@/apis/apis";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import { LIST_MODE, LIST_PER_PAGE } from "@/constant/Lists/listMode";
import useModalGetOptions from "@/CustomHooks/Modal/useModalGetOptions";
import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import SelectInput from "@/components/SelectInput/SelectInput";

const EventParticipantModal = forwardRef((_, ref) => {
  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id
    ? `${eventParticipantsApi}/${modalState.id}`
    : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Event Participant ${action} successfully.`,
    });
  };
  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePost({
    apiURL: `${eventParticipantsApi}/${modalState.id}`,
    isFormData: true,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: eventParticipantsApi,
    isFormData: true,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: EventParticipantInitialValues(
      modalState.id ? showData : null
    ),
    validationSchema: EventParticipantSchema(!!modalState.id),
    onSubmit: (values) => handleSubmit(values),
  });

  const baseQueryParams = `?mode=${LIST_MODE.SELECT}&perPage=${LIST_PER_PAGE}&filter[keyword]=`;

  const eventListApi = `${eventsMasterDataApi}${baseQueryParams}`;
  const {
    loading: eventLoading,
    defaultOptions: eventDefaultOptions,
    debouncedFetchOptions: eventFetchOptions,
    getSelectedOption: eventGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: eventListApi,
    formikValue: formik.values.event_id,
    formikInitialValue: formik.initialValues.event_id,
    showData: !!modalState?.id && showData?.data?.event,
  });

  const participantListApi = `${teamsMasterDataApi}${baseQueryParams}`;
  const {
    loading: participantLoading,
    defaultOptions: participantDefaultOptions,
    debouncedFetchOptions: participantFetchOptions,
    getSelectedOption: participantGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: participantListApi,
    formikValue: formik.values.team_id,
    formikInitialValue: formik.initialValues.team_id,
    showData: !!modalState?.id && showData?.data?.participant,
  });

  const handleSubmit = (values) => {
    const formData = new FormData();
    if (modalState.id) formData.append("_method", "PUT");
    formData.append("event_id", values.event_id);
    formData.append("team_id", values.team_id);
    formData.append("rank", values.rank);

    modalState.id ? saveEdit(formData) : saveCreate(formData);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={
        modalState.id ? "Edit Event Participant" : "Add Event Participant"
      }
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <AsyncDropdown
            label="Event"
            placeholder="No Event selected. Type to search..."
            defaultOptions={eventDefaultOptions}
            loadOptions={eventFetchOptions}
            isLoading={eventLoading}
            value={eventGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue(
                "event_id",
                selected?.value || formik.values.event_id || null
              );
            }}
            errorText={
              (formik.touched.event_id && formik.errors.event_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.event_id?.join(" "))
            }
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <AsyncDropdown
            label="Participant"
            placeholder="No participant selected. Type to search..."
            defaultOptions={participantDefaultOptions}
            loadOptions={participantFetchOptions}
            isLoading={participantLoading}
            value={participantGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue(
                "team_id",
                selected?.value || formik.values.team_id || null
              );
            }}
            errorText={
              (formik.touched.team_id && formik.errors.team_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.team_id?.join(" "))
            }
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <SelectInput
            label="Rank"
            placeholder="No rank selected."
            defaultOptions={rankTypeValues}
            value={
              rankTypeValues.find(
                (option) => option.value === formik.values.rank
              ) || null
            }
            onChange={(selected) =>
              formik.setFieldValue("rank", selected.value)
            }
            errorText={
              (formik.touched.rank && formik.errors.rank) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.rank?.join(" "))
            }
          />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default EventParticipantModal;
